import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { ModuleAccessService } from '../../services/module-access/module-access.service';

function getConfiguredUrl(route: ActivatedRouteSnapshot): string {
  return (
    '/' +
    route.pathFromRoot
      .filter((v) => v.routeConfig)
      .map((v) => v.routeConfig!.path)
      .join('/')
  );
}

export const moduleGuard: CanActivateFn = (route): Observable<boolean> => {
  const moduleAccessService = inject(ModuleAccessService);
  const router = inject(Router);
  const currentRoute = getConfiguredUrl(route);
  const newSiteOnly = route.data.newSiteOnly as boolean | undefined;

  return moduleAccessService.newSite$.pipe(
    take(1),
    switchMap((newSite) => {
      if (!newSite) {
        if (newSiteOnly) {
          console.log(
            `%c New site is not enabled on DealerUser and ${currentRoute} is only available on new site. Access denied.`,
            'background: red; color: white',
          );
          router.navigate(['/dashboard']);
          return of(false);
        } else {
          console.log(
            `%c New site is disabled on DealerUser, but ${currentRoute} is available on current site. Skipping moduleGuard.`,
            'background: green; color: white',
          );
          return of(true);
        }
      }

      console.log(
        '%c New site is enabled on DealerUser - proceeding with moduleGuard.',
        'background: black; color: white',
      );

      return moduleAccessService.moduleAccessGroups$.pipe(
        take(1),
        map((moduleGroups) => {
          if (!moduleGroups) return false;

          for (let group of moduleGroups) {
            const module = group.modules.find((module) =>
              module.routes.includes(currentRoute),
            );
            if (module) {
              console.log(
                `%c DealerUser has access to ${currentRoute}.`,
                'background: green; color: white',
              );
              return true;
            }
          }

          console.log(
            `%c DealerUser does not have access to ${currentRoute}.`,
            'background: red; color: white',
          );
          router.navigate(['/dashboard']);
          return false;
        }),
        catchError(() => {
          console.log(
            `%c Error in moduleGuard for ${currentRoute}. Access denied.`,
            'background: red; color: white',
          );
          router.navigate(['/dashboard']);
          return of(false);
        }),
      );
    }),
  );
};
